import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Hook to manage slow mode functionality for bulletins
 * Fetches settings from application_settings table and provides
 * controls for the queued posts counter
 */
export const useSlowMode = () => {
  const [isSlowModeActive, setIsSlowModeActive] = useState(false);
  const [interval, setInterval] = useState(2000); // 2 seconds between posts
  const [queuedPostsCount, setQueuedPostsCount] = useState(0);
  
  // Fetch slow mode settings from the database
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // First try localStorage as fallback
        const savedSettings = localStorage.getItem('slow_mode_settings');
        if (savedSettings) {
          try {
            const parsed = JSON.parse(savedSettings);
            setIsSlowModeActive(parsed.enabled);
            setInterval(parsed.interval);
          } catch (e) {
            console.warn('Failed to parse saved settings:', e);
          }
        }
        
        // Then try to get from database with better error handling
        const { data, error } = await supabase
          .from('application_settings')
          .select('value')
          .eq('key', 'slow_mode')
          .maybeSingle();
          
        if (error) {
          // Log error but don't crash the app
          console.warn('Error fetching slow mode settings:', error);
          return;
        }
        
        if (data && data.value) {
          setIsSlowModeActive(data.value.enabled);
          setInterval(data.value.interval);
          
          // Save to localStorage for future fallback
          localStorage.setItem('slow_mode_settings', JSON.stringify({
            enabled: data.value.enabled,
            interval: data.value.interval
          }));
        }
      } catch (err) {
        console.error('Error in fetchSettings:', err);
        // App continues to work with default settings
      }
    };

    fetchSettings();
  }, []);
  
  const updateQueuedCount = (count: number) => {
    setQueuedPostsCount(count);
  };
  
  return {
    isSlowModeActive,
    interval,
    queuedPostsCount,
    updateQueuedCount
  };
};

export default useSlowMode;
